/**
 * The shared UI vocabulary: badges, the relationship triad, modal shell, toasts, states.
 *
 * ONE implementation each. A page that lays out its own status badge or its own triad is a
 * regression of the centralization mandate, not a local convenience (UI Constitution §5).
 */

import { useEffect, useState, type ReactNode } from 'react'

import { useApp } from '../../contexts/app_context'

// --------------------------------------------------------------------------------------------
// Dates
// --------------------------------------------------------------------------------------------

/**
 * Local calendar date as YYYY-MM-DD.
 *
 * NOT `toISOString().slice(0, 10)`. That converts to UTC first, so anywhere east of Greenwich a
 * late-evening "today" becomes tomorrow, and anywhere west an early-morning "today" becomes
 * yesterday. A follow-up date that lands a day off is a missed commitment, silently.
 */
function localIso(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

export function todayIso(): string {
  return localIso(new Date())
}

export function addDaysIso(days: number, from?: string): string {
  const d = from ? new Date(`${from}T00:00:00`) : new Date()
  d.setDate(d.getDate() + days)
  return localIso(d)
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** Days from today. Negative is overdue. */
export function daysFromToday(iso: string | null | undefined): number | null {
  if (!iso) return null
  const target = new Date(`${iso.slice(0, 10)}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / 86400000)
}

// --------------------------------------------------------------------------------------------
// Badges — indicator AND label, always
// --------------------------------------------------------------------------------------------

/**
 * COLOUR REINFORCES TEXT, IT NEVER REPLACES IT (Design Tokens §3).
 * A dot with no word beside it is a defect: it fails WCAG 1.4.1 and it forces a user scanning a
 * pipeline to decode status rather than read it.
 */
export function StatusBadge({
  domain,
  value,
  fallbackTone = 'neutral',
}: {
  domain: string
  value: string | null | undefined
  fallbackTone?: string
}) {
  const { indicatorFor } = useApp()
  if (!value) return <span className="person-sub">—</span>
  const tone = indicatorFor(domain, value) || fallbackTone
  return (
    <span className={`badge b-${tone}`}>
      <span className="dot" />
      {value}
    </span>
  )
}

export type DueTone = 'overdue' | 'today' | 'upcoming' | 'waiting' | 'none'

export function dueTone(iso: string | null | undefined, waiting = false): DueTone {
  if (waiting) return 'waiting'
  const days = daysFromToday(iso)
  if (days === null) return 'none'
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  return 'upcoming'
}

const DUE_ICON: Record<DueTone, string> = {
  overdue: 'error',
  today: 'schedule',
  upcoming: 'event',
  waiting: 'hourglass_top',
  none: 'remove',
}

export function DueBadge({
  date,
  waiting = false,
  label,
}: {
  date: string | null | undefined
  waiting?: boolean
  label?: string
}) {
  const tone = dueTone(date, waiting)
  const text =
    label ??
    (tone === 'none'
      ? 'None'
      : tone === 'overdue'
        ? `${formatShortDate(date)} · ${Math.abs(daysFromToday(date)!)}d overdue`
        : tone === 'today'
          ? 'Today'
          : formatShortDate(date))
  return (
    <span className={`due d-${tone}`}>
      <span className="material-symbols-outlined">{DUE_ICON[tone]}</span>
      {text}
    </span>
  )
}

export function Tag({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>
}

export function Avatar({ name, large = false }: { name: string | null | undefined; large?: boolean }) {
  const initials = (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
  return (
    <span className={`avatar${large ? ' lg' : ''}`} aria-hidden="true">
      {initials || '?'}
    </span>
  )
}

// --------------------------------------------------------------------------------------------
// The relationship triad — ONE implementation (UI Constitution §5)
// --------------------------------------------------------------------------------------------

/**
 * Status + Next Move + Follow-Up, together.
 *
 * Wherever a relationship appears, all three appear. A screen showing one or two of the three is
 * incomplete — that is the acceptance criterion applied before anything else, and it is enforced
 * by there being exactly one component that renders them.
 */
export function RelationshipTriad({
  status,
  nextMove,
  followUpDate,
  waiting = false,
  statusDomain = 'CONTACT_STATUS',
}: {
  status: string | null | undefined
  nextMove: string | null | undefined
  followUpDate: string | null | undefined
  waiting?: boolean
  statusDomain?: string
}) {
  return (
    <div className="triad">
      <div className="triad-item">
        <span className="triad-label">Status</span>
        <span className="triad-value">
          {status ? <StatusBadge domain={statusDomain} value={status} /> : '—'}
        </span>
      </div>
      <div className="triad-item">
        <span className="triad-label">Next Move</span>
        <span className="triad-value move">{nextMove || '— none recorded —'}</span>
      </div>
      <div className="triad-item">
        <span className="triad-label">Follow-Up</span>
        <span className="triad-value">
          <DueBadge date={followUpDate} waiting={waiting} />
        </span>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------------------------
// Modal — header / body / footer, one shell (BlueHomer §2.6)
// --------------------------------------------------------------------------------------------

export function ModalShell({
  title,
  description,
  onClose,
  footer,
  wide = false,
  children,
}: {
  title: string
  description?: string
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{title}</div>
            {description && <div className="modal-desc">{description}</div>}
          </div>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {/* Cancel is always present and is distinct from Save (BlueHomer §3.25). */}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------------------------
// Toasts — never window.alert, never window.confirm (BlueHomer §3.23, §3.30)
// --------------------------------------------------------------------------------------------

export function ToastHost() {
  const { toasts, dismiss, reload } = useApp()
  const [retrying, setRetrying] = useState<number | null>(null)
  if (!toasts.length) return null
  return (
    <div className="toast-host">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.tone}`} role="status">
          <span className="material-symbols-outlined" aria-hidden="true">
            {t.tone === 'ok' ? 'check_circle' : t.tone === 'err' ? 'error' : 'info'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="toast-title">{t.title}</div>
            {t.body && <div className="toast-body">{t.body}</div>}
            {/* Copyable technical detail — an error the user cannot forward is one nobody fixes. */}
            {t.technical && <pre className="toast-tech">{t.technical}</pre>}
            {/* For failures that are usually transient. Without this the only way out is for the
                user to work out that a page reload would fix it. */}
            {t.retry && (
              <button
                className="btn secondary sm"
                type="button"
                style={{ marginTop: 8 }}
                disabled={retrying === t.id}
                onClick={async () => {
                  setRetrying(t.id)
                  try {
                    await reload()
                    dismiss(t.id)
                  } finally {
                    setRetrying(null)
                  }
                }}
              >
                <span className="material-symbols-outlined">refresh</span>
                {retrying === t.id ? 'Retrying…' : 'Try again'}
              </button>
            )}
          </div>
          <button className="toast-x" type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      ))}
    </div>
  )
}

// --------------------------------------------------------------------------------------------
// States
// --------------------------------------------------------------------------------------------

export function Spinner() {
  return (
    <div className="page-spinner">
      <div className="spinner" role="status" aria-label="Loading" />
    </div>
  )
}

export function EmptyState({
  icon = 'inbox',
  title,
  message,
  action,
}: {
  icon?: string
  title: string
  message?: string
  action?: ReactNode
}) {
  return (
    <div className="empty-state">
      <span className="material-symbols-outlined">{icon}</span>
      <h3>{title}</h3>
      {message && <p>{message}</p>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  )
}

export function ErrorState({ title, message, technical, onRetry }: {
  title: string
  message?: string
  technical?: string
  onRetry?: () => void
}) {
  return (
    <div className="banner block" style={{ margin: '16px 0' }}>
      <span className="material-symbols-outlined">error</span>
      <div style={{ flex: 1 }}>
        <b>{title}</b>
        {message && <div style={{ marginTop: 4 }}>{message}</div>}
        {technical && <pre className="toast-tech">{technical}</pre>}
        {onRetry && (
          <button className="btn secondary sm" type="button" style={{ marginTop: 9 }} onClick={onRetry}>
            <span className="material-symbols-outlined">refresh</span>Try again
          </button>
        )}
      </div>
    </div>
  )
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  danger = false,
  onConfirm,
  onCancel,
}: {
  title: string
  message: string
  confirmLabel?: string
  danger?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <ModalShell
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className={`btn ${danger ? 'danger' : 'primary'}`} type="button" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-relaxed)' }}>{message}</p>
    </ModalShell>
  )
}
